<template>
  <div :class="$attrs">
    <v-input
      :disabled="disabled || waitingForAi"
      hide-details
      class="mb-2"
    >
      <template #prepend>
        <slot name="prepend" />
      </template>
      <div class="editor-shell flex-grow-1">
        <div
          v-if="waitingForAi"
          class="editor-ai-placeholder"
          :class="{ 'editor-ai-placeholder--heading': isHeading }"
        >
          <v-progress-linear
            indeterminate
            height="2"
            color="primary"
            class="mb-3"
          />
          <span class="editor-ai-placeholder__text">{{ $t('article.openAi.loading') }}</span>
        </div>
        <ckeditor
          v-else
          v-model="localValue"
          :editor="editor"
          :config="config"
          :words-to-highlight="wordsToHighlight"
          :disabled="disabled"
          class="flex-grow-1 focusedStyleOnHover ck-border"
          :class="{
            'text-h5' : isHeading,
            'ck-small-margin': isHeading,
            'ck-text-bold': isHeading
          }"
        />
      </div>
      <template #append>
        <slot name="append" />
      </template>
    </v-input>
  </div>
</template>

<script lang="ts">
    import Vue, {PropType} from 'vue';
    import { InlineEditor } from '@ckeditor/ckeditor5-editor-inline';
    import {articleEditorPlugins} from '@/service/articleEditorHtml';

    export default Vue.extend({
        name: 'Editor',
        props: {
            value: {
                type: String,
                default: () => ''
            },
            placeholder: {
                type: String,
                default: null
            },
            wordsToHighlight: {
                type: Object as PropType<{ [key: string]: string[] }>,
                default: () => ({})
            },
            shouldListenForChange: {
                type: Boolean,
                default: false
            },
            waitingForAi: {
                type: Boolean,
                default: false
            },
            allowFormatting: {
                type: Boolean,
                default: true
            },
            disabled: {
                type: Boolean,
                default: false
            },
            isHeading: {
                type: Boolean,
                default: false
            }
        },
        // emits: ['input'],
        data: () => ({
            editor: InlineEditor,
            localValue: '',
        }),
        computed: {
            config: function (): any {
                return {
                    placeholder: this.placeholder,
                    toolbar: this.toolbar,
                    plugins: articleEditorPlugins.slice(),
                };
            },
            toolbar: function (): string[] {
                if (!this.allowFormatting) {
                    return ['undo', 'redo'];
                }
                return [
                    'undo', 'redo', '|',
                    'bold', 'italic', '|',
                    'numberedList', 'bulletedList', '|',
                    'outdent', 'indent', '|',
                    'link'
                ];
            }
        },
        watch: {
            localValue: function () {
                this.$emit('input', this.localValue);
            },
            value: {
                immediate: true,
                handler: function () {
                    if (this.value !== null && this.shouldListenForChange && this.value !== this.localValue) {
                        this.localValue = this.value;
                    }
                }
            }
        },
        created: function () {
            if (this.value) {
                this.localValue = this.value;
            }
        }
    });
</script>

<style lang="sass">
/*
 * source: CKEditor documentation - https://ckeditor.com/docs/ckeditor5/latest/builds/guides/integration/frameworks/css.html
 */
body
    --ck-z-default: 100
    --ck-z-modal: calc(var(--ck-z-default) + 999)

.focusedStyleOnHover:hover
    outline: none
    border: var(--ck-focus-ring)
    box-shadow: var(--ck-inner-shadow),0 0

.ck.ck-content strong
    font-weight: bold

.ck.ck-content ul,
.ck.ck-content ul li
    list-style-type: inherit

.ck.ck-content ul
    padding-left: 40px

.ck.ck-content ol,
.ck.ck-content ol li
    list-style-type: decimal

.ck.ck-content ol
    padding-left: 40px

.ck.ck-content ul,
.ck.ck-content ul li
    list-style-type: inherit

.ck.ck-content ul
    padding-left: 40px

.ck.ck-content ol,
.ck.ck-content ol li
    list-style-type: decimal

.ck.ck-content ol
    padding-left: 40px

.ck input.ck-input.ck-input-text
    box-shadow: var(--ck-inner-shadow), 0 0
    background: var(--ck-color-input-background)
    border: 1px solid var(--ck-color-input-border)
    padding: var(--ck-spacing-extra-tiny) var(--ck-spacing-medium)
    transition-property: box-shadow, border
    transition: .2s ease-in-out

    height: inherit
    width: inherit
    font-size: inherit
    margin: 0
    box-sizing: border-box

.ck input.ck-input.ck-input-text:focus
    border: var(--ck-focus-ring)
    box-shadow: var(--ck-focus-outer-shadow), var(--ck-inner-shadow)

.ck-border
    // border-bottom: 1px solid var(--v-primary-base) !important
    margin-bottom: -4px

.ck-small-margin p
    // Same as calculated for basic 'p'
    margin-bottom: 14.4px !important
    margin-top: 14.4px !important

.ck-text-bold p
    font-weight: 600

    strong
        font-weight: 800 !important

.editor-shell
    width: 100%

.editor-ai-placeholder
    min-height: 88px
    padding: 12px 4px 8px
    border-bottom: 1px solid rgba(8, 135, 220, 0.28)

.editor-ai-placeholder--heading
    min-height: 52px
    padding-top: 6px

.editor-ai-placeholder__text
    display: block
    color: var(--v-primary-base, #0887dc)
    font-size: 0.875rem
    line-height: 1.4
    opacity: 0.85

.ck.ck-content table
    border-collapse: collapse
    width: 100%
    margin: 8px 0

.ck.ck-content td,
.ck.ck-content th
    border: 1px solid rgba(0, 0, 0, 0.2)
    padding: 4px 6px
    vertical-align: top
</style>
